import {
  addTransactionSignature,
  changeSignatureFromFirstRank,
  updateFileWithPageNumber,
} from "@/state/transactions/CurrentTransactionEvents";
import { SignaturePatternEnum } from "@/utils/enums/signature/signature.pattern.enum";
import BasicButton from "@components/core/buttons/BasicButton";
import InfoModal from "@components/core/modals/InfoModal";
import PdfViewer from "@components/core/pdf/PdfViewer";
import ModelSignatoryCard from "@components/core/subComponents/ModelSignatoryCard";
import {
  File,
  Signatory,
  Signature,
} from "@type/core/dashboard/dashboard.types";
import { CONSEIL_EXPERT_COMPTABLES } from "@utils/Constant";
import { Col, Row, Spin } from "antd";
import { Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import SignatureHelper from "@utils/helpers/signature-helper";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import useSignatoryCardDimension from "@utils/hooks/useSignatoryCardDimension";

interface ModelPdfContainerProps {
  index: number;
  file: File;
  numberOfFiles: number;
  transactionType: string;
  signatories: Signatory[];
  signatures: Signature[];
  manualPositioning: boolean;
  setManualPositioning: Dispatch<SetStateAction<boolean>>;
  buttonLoading?: boolean;
  setButtonLoading?: Dispatch<SetStateAction<boolean>>;
  className?: string;
}

const ModelPdfContainer: FunctionComponent<ModelPdfContainerProps> = ({
  index,
  file,
  numberOfFiles,
  transactionType,
  signatories,
  signatures,
  setManualPositioning,
  buttonLoading,
  className = "",
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(true);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [positioningModalVisible, setPositioningModalVisible] =
    useState<boolean>(false);

  const src = file.url;
  const signaturesForThisDocument = signatures.filter(
    (signature) => signature.rank === index,
  );
  const { width, height } = useSignatoryCardDimension({
    rank: index,
    pageNumber,
  });

  const setTotalPageNumber = (totalPageNumber: number) => {
    file.pages = totalPageNumber;
    updateFileWithPageNumber(file);
  };

  const handleAddSignature = () => {
    const nbSignatories = signatures.length / numberOfFiles + 1;
    const id = "Signature" + nbSignatories;

    for (let i = 0; i < numberOfFiles; i++) {
      const newSignature: Signature = {
        id: id,
        email: null,
        name: id,
        rank: i + 1,
        positionX: 0,
        positionY: 0,
        isMoved: false,
        positioning: false,
        page: 0,
        order: nbSignatories,
        pattern: SignaturePatternEnum.DEFAULT,
        identificator: SignatureHelper.buildSignatureIdentificator(
          i,
          nbSignatories - 1,
          0,
        ),
      };
      addTransactionSignature(newSignature);
    }
  };

  const handleApplySignaturePositionToEveryFiles = () => {
    changeSignatureFromFirstRank();
    setPositioningModalVisible(false);
  };

  return (
    <div className={`app-pdf-container-model ${className}`}>
      <h2 title={file.name} className="overflow-ellipsis">
        {file.name}
      </h2>
      <Row gutter={15} className="pdf-container" id={"document_" + index}>
        <Col span={24} sm={12} data-test="document" id={"leftPanel_" + index}>
          {src && (
            <PdfViewer
              url={src}
              setLoading={setLoading}
              onPageChange={setPageNumber}
              getTotalPageNumber={setTotalPageNumber}
            />
          )}
        </Col>
        <Col span={24} sm={12} id={"rightPanel_" + index}>
          {!loading ? (
            <>
              <div
                title={
                  transactionType === "signature"
                    ? t("signature.positioning.place", { count: 0 })
                    : t("scellement.positioning.place")
                }
                className="text-nowrap h-25px"
              >
                <span className="d-none d-md-inline">
                  {transactionType === "signature"
                    ? t("signature.positioning.place", { count: 0 })
                    : t("scellement.positioning.place")}
                </span>
                <span className="d-md-none">{t("signature.signatures")}</span>
                <span>
                  &nbsp;
                  {"(" +
                    signatures.filter(
                      (signature) =>
                        signature.rank === index && signature.isMoved,
                    ).length}
                  /
                  {transactionType === "signature"
                    ? signatures.filter((signature) => signature.rank === index)
                        .length
                    : 1}
                  )
                </span>
              </div>
              {transactionType === "signature" &&
                signaturesForThisDocument.map((signature, indexSignatory) => {
                  if (src) {
                    const signatory = signatories.find(
                      (s) => s.email === signature.email,
                    );

                    return (
                      <div
                        className="position-relative"
                        key={
                          "pdf_" + (index - 1) + "_signatory_" + indexSignatory
                        }
                      >
                        <ModelSignatoryCard
                          cardDimensionHeight={height}
                          cardDimensionWidth={width}
                          documentRank={index}
                          transactionType={transactionType}
                          signature={signature}
                          signatures={signatures}
                          pageNumber={pageNumber}
                          setManualPositioning={setManualPositioning}
                          firstname={signatory?.firstname ?? ""}
                          lastname={signatory?.lastname ?? ""}
                          signatureIdentificator={SignatureHelper.buildSignatureIdentificator(
                            index - 1,
                            indexSignatory,
                            0,
                          )}
                        />
                      </div>
                    );
                  } else {
                    return null;
                  }
                })}
              {index === 1 && transactionType === "signature" && (
                <div className="position-absolute d-flex align-items-center">
                  <BasicButton
                    variant="tertiary"
                    text={t("signature.positioning.addSignature")}
                    className="mw-300 mt-16 mr-4 py-8 px-32 mh-42 font-extra-small"
                    onClick={handleAddSignature}
                  />
                </div>
              )}
              {transactionType === "scellement" && src && (
                <ModelSignatoryCard
                  cardDimensionHeight={height}
                  cardDimensionWidth={width}
                  documentRank={1}
                  transactionType={transactionType}
                  signature={signatures[0]}
                  signatures={signatures}
                  setManualPositioning={setManualPositioning}
                  pageNumber={pageNumber}
                  firstname={CONSEIL_EXPERT_COMPTABLES}
                  lastname=""
                  signatureIdentificator={SignatureHelper.buildSignatureIdentificator(
                    0,
                    0,
                    0,
                  )}
                />
              )}
              {index === 1 &&
                numberOfFiles > 1 &&
                transactionType === "signature" && (
                  <div className="position-absolute d-flex align-items-center mb-16 mt-64">
                    <BasicButton
                      disabled={buttonLoading}
                      isLoading={buttonLoading}
                      variant="tertiary"
                      text={t("signature.positioning.applyFirstToOthers")}
                      className="mw-300 mr-4 py-8 px-30 mh-42 font-extra-small"
                      onClick={() => setPositioningModalVisible(true)}
                    />
                    <InfoModal value="positioning" info />
                    <ConfirmModal
                      title={t("signature.positioning.manualBody")}
                      visible={positioningModalVisible}
                      onOk={handleApplySignaturePositionToEveryFiles}
                      onCancel={() => setPositioningModalVisible(false)}
                    />
                  </div>
                )}
            </>
          ) : (
            <div className="h-100 flex-grow-1 d-flex align-items-center justify-content-center">
              <Spin />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ModelPdfContainer;
